import { useEffect, type ReactNode } from "react";
import {
  BadgeCheckIcon,
  FlameIcon,
  Maximize2Icon,
  Minimize2Icon,
  type LucideIcon,
} from "lucide-react";
import { Link } from "react-router";

import { userSheetViewStore } from "@/stores/userSheetView";
import type { UserSheetPipeline, UserSheetSubmission } from "./types";

import { Center, Column, RichTextDisplay, Row, Text } from "@/components";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

import { useIsMobile } from "@/hooks/use-mobile";
import {
  candidacyStatusToBackgroundColor,
  candidacyStatusToLabel,
} from "@/lib/constants";
import { dates } from "@/lib/dates";
import { formatting } from "@/lib/formatting";
import { cn } from "@/lib/utils";
import type { CandidacySource, CandidacyStatus } from "@/types";

// Right-hand rail of the profile tab: what this user is currently up to.
// Collapses both cards at once into count tiles so the profile body can take
// the full width.
export const UserSheetActivityRail = ({
  isCollapsible = true,
  pipelines,
  recentActivityAt,
  showPipelines = true,
  showSubmissions = true,
  submissions,
}: {
  isCollapsible?: boolean;
  pipelines: UserSheetPipeline[];
  recentActivityAt?: string | null;
  showPipelines?: boolean;
  showSubmissions?: boolean;
  submissions: UserSheetSubmission[];
}) => {
  /* Context */
  // Collapse state lives in the view store so it survives tab remounts and
  // focus mode can save/restore it.
  const isCollapsed = userSheetViewStore((s) => s.isActivityRailCollapsed);
  const setIsCollapsed = userSheetViewStore(
    (s) => s.setIsActivityRailCollapsed,
  );
  const isSplitView = userSheetViewStore((s) => s.splitTabValues.length > 0);
  const canCollapse = isCollapsible;
  const isMobile = useIsMobile();

  /* React Hooks */
  // A split pane has no room for the cards, so they start as tiles there —
  // expanding one afterwards stays the reader's call.
  useEffect(() => {
    if (!canCollapse) return;
    if (isSplitView) setIsCollapsed(true);
  }, [canCollapse, isSplitView, setIsCollapsed]);

  /* Render */
  const renderCard = ({
    children,
    Icon,
    iconClassName,
    title,
  }: {
    children: ReactNode;
    Icon: LucideIcon;
    iconClassName: string;
    title: string;
  }) => {
    return (
      <Column className="group/rail-card gap-4 rounded-xl border border-slate-100 bg-slate-50 p-4 shadow-sm">
        <Row className="items-center justify-between gap-3">
          <Text
            className={cn(
              "font-plex-mono font-medium uppercase tracking-wider text-slate-400",
              isMobile ? "text-xs" : "text-[11px]",
            )}
          >
            {title}
          </Text>
          <Row className="relative h-6 w-6 shrink-0 items-center justify-center">
            <Icon
              className={cn(
                "h-3.5 w-3.5 transition-opacity",
                canCollapse && "group-hover/rail-card:opacity-0",
                iconClassName,
              )}
            />
            {canCollapse ? <Button
              className="absolute inset-0 h-6 w-6 opacity-0 transition-opacity group-hover/rail-card:opacity-100"
              onClick={() => {
                setIsCollapsed(true);
              }}
              size="icon"
              title="Minimize profile activity"
              type="button"
              variant="ghost"
            >
              <Minimize2Icon className="h-3.5 w-3.5 text-slate-500" />
            </Button> : null}
          </Row>
        </Row>
        {children}
      </Column>
    );
  };

  const renderCollapsedTile = ({
    count,
    expandLabel,
    Icon,
    iconClassName,
  }: {
    count: number;
    expandLabel: string;
    Icon: LucideIcon;
    iconClassName: string;
  }) => {
    return (
      <TooltipProvider delayDuration={200}>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              className="relative h-12 w-12 rounded-xl bg-slate-100 text-slate-500 hover:bg-slate-200 hover:text-slate-700"
              onClick={() => {
                setIsCollapsed(false);
              }}
              size="icon"
              type="button"
              variant="ghost"
            >
              <Icon className={cn("h-4 w-4", iconClassName)} />
              <Center className="absolute -right-1 -top-1 h-5 min-w-5 rounded-full bg-slate-600 px-1 text-[10px] font-semibold text-white">
                {count}
              </Center>
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            <Row className="items-center gap-1.5">
              <Maximize2Icon className="h-3 w-3" />
              <Text className="text-xs font-normal">{expandLabel}</Text>
            </Row>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  };

  const renderStageChip = (status: CandidacyStatus) => {
    return (
      <Row className="h-6 w-fit shrink-0 items-center gap-1.5 rounded-full border border-slate-300 bg-white px-2 shadow-sm">
        <Center
          className={cn(
            "h-2 w-2 shrink-0 rounded-full",
            candidacyStatusToBackgroundColor[status],
          )}
        >
          {null}
        </Center>
        <Text className="text-[11px] font-medium text-slate-800">
          {candidacyStatusToLabel[status]}
        </Text>
      </Row>
    );
  };

  const renderSourceChip = (source: CandidacySource) => {
    return (
      <Row className="h-6 w-fit shrink-0 items-center rounded-full border border-slate-300 bg-white px-2 shadow-sm">
        <Text className="text-[11px] font-medium text-slate-800">
          {formatting.candidacySourceLabel(source)}
        </Text>
      </Row>
    );
  };

  const renderPipelinesCard = () => {
    return renderCard({
      children: (
        <>
          {pipelines.length > 0 ? (
            <Column className="divide-y divide-slate-200">
              {pipelines.map((pipeline) => (
                <Link
                  className="group/pipeline block py-3 first:pt-0 last:pb-0"
                  key={pipeline.candidacyId}
                  rel="noopener"
                  target="_blank"
                  to={`/companies/${pipeline.companyId}/jobs/${pipeline.jobId}?c=${pipeline.candidacyId}`}
                >
                  <Column className="gap-2">
                    <Text
                      className={cn(
                        "font-medium text-slate-800 group-hover/pipeline:underline",
                        isMobile ? "text-base" : "text-sm",
                      )}
                    >
                      {pipeline.title}
                    </Text>
                    <Text className="text-xs font-normal text-slate-400">
                      {pipeline.company}
                    </Text>
                    {renderStageChip(pipeline.status)}
                  </Column>
                </Link>
              ))}
            </Column>
          ) : (
            <Text className="text-sm font-normal text-slate-500">
              No active pipelines.
            </Text>
          )}
          {recentActivityAt ? (
            <Text className="text-xs font-normal text-slate-400">
              Last active {dates.formatShortDate(recentActivityAt)}
            </Text>
          ) : null}
        </>
      ),
      Icon: FlameIcon,
      iconClassName: "text-orange-500",
      title: "Active Pipelines",
    });
  };

  const renderSubmissionsCard = () => {
    return renderCard({
      children:
        submissions.length > 0 ? (
          <Column className="divide-y divide-slate-100">
            {submissions.map((submission) => (
              <Link
                className="group/submission block py-3 first:pt-0 last:pb-0"
                key={`${submission.candidacyId}-${submission.submittedAt}`}
                rel="noopener"
                target="_blank"
                to={`/companies/${submission.companyId}/jobs/${submission.jobId}?c=${submission.candidacyId}`}
              >
                <Column className="gap-1">
                  <Row className="items-end justify-between gap-3">
                    <Text
                      className={cn(
                        "font-medium text-slate-800 group-hover/submission:underline",
                        isMobile ? "text-base" : "text-sm",
                      )}
                    >
                      {submission.title}
                    </Text>
                    <Text className="shrink-0 text-xs font-normal text-slate-400">
                      {dates.formatShortDate(submission.submittedAt)}
                    </Text>
                  </Row>
                  <Text className="text-xs font-normal text-slate-400">
                    {submission.company}
                  </Text>
                  {submission.postSubmissionStatus || submission.source ? (
                    <Column className="gap-1 pt-1">
                      <Row className="flex-wrap items-center gap-1.5">
                        {submission.postSubmissionStatus
                          ? renderStageChip(submission.postSubmissionStatus)
                          : null}
                        {submission.source
                          ? renderSourceChip(submission.source)
                          : null}
                      </Row>
                      {submission.postSubmissionReason ? (
                        // Rejection/status-change reasons are stored as rich
                        // text, so render the HTML instead of showing raw tags.
                        <RichTextDisplay
                          className="text-xs font-normal text-slate-500"
                          richText={submission.postSubmissionReason}
                        />
                      ) : null}
                    </Column>
                  ) : null}
                </Column>
              </Link>
            ))}
          </Column>
        ) : (
          <Text className="text-sm font-normal text-slate-500">
            No submissions yet.
          </Text>
        ),
      Icon: BadgeCheckIcon,
      iconClassName: "text-primary-900",
      title: "Submissions",
    });
  };

  const renderContent = () => {
    if (canCollapse && isCollapsed) {
      return (
        <>
          {showPipelines ? renderCollapsedTile({
            count: pipelines.length,
            expandLabel: "Expand active pipelines",
            Icon: FlameIcon,
            iconClassName: "text-orange-500",
          }) : null}
          {showSubmissions ? renderCollapsedTile({
            count: submissions.length,
            expandLabel: "Expand submissions",
            Icon: BadgeCheckIcon,
            iconClassName: "text-primary-900",
          }) : null}
        </>
      );
    }

    return (
      <>
        {showPipelines ? renderPipelinesCard() : null}
        {showSubmissions ? renderSubmissionsCard() : null}
      </>
    );
  };

  return (
    <Column
      className={cn(
        "w-full shrink-0 transition-[width] duration-500 ease-out",
        canCollapse && isCollapsed
          ? "items-end gap-3 self-start lg:w-12"
          : "gap-6 lg:w-1/3",
      )}
    >
      {renderContent()}
    </Column>
  );
};
